import { collection, doc, getDocs, query, updateDoc, where } from "firebase/firestore";
import { db } from "../../firebase/config";
import {
  MAHESH_L2_SERIAL_FLOW_REF,
  maheshL2SerialRouteForPaymentIndex,
} from "../../config/autoFillMaheshL2SerialConfig";
import { buildTransactionId, recordAutoFillLedgerEntry } from "./autoFillLedger";

async function getUserDocByUid(uid) {
  const snap = await getDocs(
    query(collection(db, "users"), where("uid", "==", uid))
  );
  if (snap.empty) return null;
  return { id: snap.docs[0].id, data: snap.docs[0].data() };
}

async function findProgramByPublicId(programId) {
  const snap = await getDocs(
    query(collection(db, "autoFillPrograms"), where("programId", "==", programId))
  );
  if (snap.empty) return null;
  return { docId: snap.docs[0].id, ...snap.docs[0].data() };
}

async function creditPoints(userDocRef, userData, amount) {
  const points = userData.autoFillPoints || {
    earned: 0,
    available: 0,
    withdrawn: 0,
    locked: 0,
  };
  await updateDoc(userDocRef, {
    autoFillPoints: {
      ...points,
      earned: (points.earned || 0) + amount,
      available: (points.available || 0) + amount,
    },
    updatedAt: new Date().toISOString(),
  });
}

async function creditUpgrade(userDocRef, userData, amount) {
  const upgrade = userData.autoFillUpgrade || { reserved: 0, used: 0 };
  await updateDoc(userDocRef, {
    autoFillUpgrade: {
      reserved: (upgrade.reserved || 0) + amount,
      used: upgrade.used || 0,
    },
    updatedAt: new Date().toISOString(),
  });
}

/**
 * Mahesh L2 pay #1–3: credit AFP-000014 / 015 / 016 (not mahesh owner wallet).
 */
export async function applyMaheshL2SerialPaymentCredit({
  paymentIndex,
  amount,
  fillerProgramId,
  maheshProgramId,
  orderId,
  matrixPositionId,
  fillerUserId,
}) {
  const route = maheshL2SerialRouteForPaymentIndex(paymentIndex);
  if (!route) {
    return { ok: false, reason: "invalid_payment_index" };
  }
  if (route.programId !== fillerProgramId) {
    return {
      ok: false,
      reason: "wrong_filler_for_mahesh_L2_serial",
      expectedFiller: route.programId,
    };
  }

  const recipient = await findProgramByPublicId(route.programId);
  if (!recipient) {
    return { ok: false, reason: "recipient_program_missing", programId: route.programId };
  }

  const userDoc = await getUserDocByUid(recipient.userId);
  if (!userDoc) {
    return { ok: false, reason: "recipient_user_missing" };
  }

  const ledgerMeta = {
    matrixPaymentIndex: paymentIndex,
    fillerProgramId,
    fillSource: MAHESH_L2_SERIAL_FLOW_REF,
    maheshL2SerialFlow: true,
    maheshProgramId,
    allocation: route.allocation,
    allocationLevel: route.recipientLevel,
    triggerProgramId: maheshProgramId,
  };

  if (route.allocation === "WITHDRAWAL") {
    await creditPoints(doc(db, "users", userDoc.id), userDoc.data, amount);
    await recordAutoFillLedgerEntry({
      transactionId: buildTransactionId("M5-L2"),
      programId: recipient.programId,
      userId: recipient.userId,
      level: route.recipientLevel,
      amount,
      transactionType: "POINTS",
      fromWallet: "MATRIX_POOL",
      toWallet: "POINTS",
      matrixPositionId,
      fillerUserId,
      reference: orderId || `${MAHESH_L2_SERIAL_FLOW_REF}:${maheshProgramId}:pay${paymentIndex}`,
      metadata: ledgerMeta,
    });
  } else {
    await creditUpgrade(doc(db, "users", userDoc.id), userDoc.data, amount);
    await recordAutoFillLedgerEntry({
      transactionId: buildTransactionId("M5-L2"),
      programId: recipient.programId,
      userId: recipient.userId,
      level: route.recipientLevel,
      amount,
      transactionType: "LEVEL_UPGRADE",
      fromWallet: "MATRIX_POOL",
      toWallet: "UPGRADE",
      matrixPositionId,
      fillerUserId,
      reference: orderId || `${MAHESH_L2_SERIAL_FLOW_REF}:${maheshProgramId}:pay${paymentIndex}`,
      metadata: {
        ...ledgerMeta,
        matrixPaymentIndex: paymentIndex,
        role:
          paymentIndex === 2 ? "LEVEL_UNLOCK_RESERVE" : "LEVEL_UNLOCK_FINAL",
      },
    });
  }

  return {
    ok: true,
    recipientProgramId: recipient.programId,
    recipientLevel: route.recipientLevel,
    allocation: route.allocation,
  };
}

export async function maheshL2SerialCycleFilled(maheshProgramDocId) {
  const snap = await getDocs(
    query(
      collection(db, "autoFillMatrixPositions"),
      where("programDocId", "==", maheshProgramDocId),
      where("level", "==", 2),
      where("positionStatus", "==", "filled")
    )
  );
  if (snap.size < 3) return false;
  const ordered = snap.docs
    .map((d) => d.data())
    .sort(
      (a, b) =>
        new Date(a.filledAt || 0).getTime() - new Date(b.filledAt || 0).getTime()
    );
  for (let i = 0; i < 3; i += 1) {
    const expected = maheshL2SerialRouteForPaymentIndex(i + 1)?.programId;
    if (ordered[i]?.filledByProgramId !== expected) return false;
  }
  return true;
}
